import React, { memo, useMemo } from "react";
import { Image } from "antd";

interface IPostGalleryImage {
	id: number;
	image: string;
	mobile?: string;
	desktop?: {
		xs?: string;
		sm?: string;
		md?: string;
		l?: string;
		xl?: string;
	};
}

interface IPostGallery {
	images: IPostGalleryImage[];
}

/**
 * Facebook-style multi-image gallery for feed posts (#1279). Lays out 2–4+
 * thumbnails in a responsive grid and opens a swipeable lightbox (antd
 * Image.PreviewGroup) showing every image. Single-image posts do NOT use this
 * component — they keep the existing PostImage behaviour unchanged.
 */
const PostGallery: React.FunctionComponent<IPostGallery> = ({ images }) => {
	const urls = useMemo(
		() => images.map((i) => i.image).filter(Boolean),
		[images],
	);

	// Up to 4 tiles are shown; a 5th+ collapses into a "+N" overlay on the last
	// visible tile. All images stay in the preview group so the lightbox shows
	// the full set.
	const visible = urls.slice(0, 4);
	const hidden = urls.slice(4);
	const extra = hidden.length;

	// All rows locked to the same fixed height so images crop consistently
	// regardless of their original dimensions (tiles use object-fit: cover).
	const ROW_HEIGHT = "200px";
	const gridTemplate = (() => {
		switch (visible.length) {
			case 1:
				return { columns: "1fr", rows: ROW_HEIGHT };
			case 2:
				return { columns: "1fr 1fr", rows: ROW_HEIGHT };
			case 3:
				return {
					columns: "2fr 1fr",
					rows: `${ROW_HEIGHT} ${ROW_HEIGHT}`,
				};
			default:
				return {
					columns: "1fr 1fr",
					rows: `${ROW_HEIGHT} ${ROW_HEIGHT}`,
				};
		}
	})();

	const tileStyle: React.CSSProperties = {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		display: "block",
		borderRadius: 6,
		cursor: "pointer",
	};

	return (
		// 12px gap before the grid; 16px after it, before the reactions bar.
		<div style={{ marginTop: 12, marginBottom: 16 }}>
			<Image.PreviewGroup>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: gridTemplate.columns,
						gridTemplateRows: gridTemplate.rows,
						// #1444 between-photos token: 8px gutter in the grid.
						gap: 8,
					}}
				>
					{visible.map((url, idx) => {
						// In the 3-image layout the first tile spans both rows.
						const spanRows =
							visible.length === 3 && idx === 0 ? 2 : 1;
						const isLastVisible = idx === visible.length - 1;
						return (
							<div
								key={url}
								style={{
									position: "relative",
									gridRow:
										spanRows === 2 ? "span 2" : undefined,
									overflow: "hidden",
									borderRadius: 6,
								}}
							>
								<Image
									src={url}
									alt=""
									wrapperStyle={{
										width: "100%",
										height: "100%",
									}}
									style={tileStyle}
								/>
								{isLastVisible && extra > 0 && (
									<div
										style={{
											position: "absolute",
											inset: 0,
											background: "rgba(0,0,0,0.45)",
											color: "#fff",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: 28,
											fontWeight: 700,
											pointerEvents: "none",
											borderRadius: 6,
										}}
									>
										+{extra}
									</div>
								)}
							</div>
						);
					})}
				</div>
				{/* Keep the overflow images in the preview group (not laid out). */}
				<div style={{ display: "none" }}>
					{hidden.map((url) => (
						<Image key={url} src={url} alt="" />
					))}
				</div>
			</Image.PreviewGroup>
		</div>
	);
};

export default memo(PostGallery);
